import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

function LocSelect({ label, options, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger className="mt-1" data-testid={testid}><SelectValue placeholder="—" /></SelectTrigger>
        <SelectContent>
          {(options || []).map(function(o) { return <SelectItem key={o.value} value={o.value}>{o.value}{o.capacity ? ' (cap ' + o.capacity + ')' : ''}</SelectItem>; })}
        </SelectContent>
      </Select>
    </div>
  );
}

export function CatalogDialog({ open, onOpenChange, form, setForm, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add Catalog Item</DialogTitle></DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Item Code *</Label>
              <Input value={form.item_code} onChange={function(e) { setForm({...form, item_code: e.target.value}); }} className="mt-1" data-testid="catalog-item-code" />
            </div>
            <div>
              <Label>System Type</Label>
              <Select value={form.system_type} onValueChange={function(v) { setForm({...form, system_type: v}); }}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ykk">YKK Bhoruka</SelectItem>
                  <SelectItem value="unitized">Unitized Glazing</SelectItem>
                  <SelectItem value="other">Other Aluminum</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Item Name *</Label>
            <Input value={form.item_name} onChange={function(e) { setForm({...form, item_name: e.target.value}); }} className="mt-1" data-testid="catalog-item-name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={function(v) { setForm({...form, category: v}); }}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="profile">Profile</SelectItem>
                  <SelectItem value="hardware">Hardware</SelectItem>
                  <SelectItem value="gasket">Gasket</SelectItem>
                  <SelectItem value="glass">Glass</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Unit of Measure</Label>
              <Select value={form.uom} onValueChange={function(v) { setForm({...form, uom: v}); }}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="meters">Meters</SelectItem>
                  <SelectItem value="pieces">Pieces</SelectItem>
                  <SelectItem value="kg">Kg</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Item Type</Label>
              <Select value={form.item_type || 'profile'} onValueChange={function(v) { setForm({ ...form, item_type: v }); }}>
                <SelectTrigger className="mt-1" data-testid="catalog-item-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="profile">Profile</SelectItem><SelectItem value="hardware">Hardware</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>Finish</Label>
              <Input value={form.finish || ''} onChange={function(e) { setForm({ ...form, finish: e.target.value }); }} className="mt-1" />
            </div>
            <div>
              <Label>Material Type</Label>
              <Input value={form.material_type || ''} onChange={function(e) { setForm({ ...form, material_type: e.target.value }); }} className="mt-1" />
            </div>
            <div>
              <Label>Length (m)</Label>
              <Input type="number" value={form.length_m || 0} onChange={function(e) { setForm({ ...form, length_m: parseFloat(e.target.value) || 0 }); }} className="mt-1" disabled={form.item_type === 'hardware'} />
            </div>
          </div>
          <div>
            <Label>Minimum Stock Level</Label>
            <Input type="number" value={form.minimum_stock_level} onChange={function(e) { setForm({...form, minimum_stock_level: parseFloat(e.target.value) || 0}); }} className="mt-1" />
          </div>
          <Button type="submit" className="w-full btn-accent" data-testid="save-catalog-btn">Add to Catalog</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function InwardDialog({ open, onOpenChange, form, setForm, catalog, master, onSubmit }) {
  var wl = form.warehouse_location || {};
  var setWL = function(field, v) { setForm({ ...form, warehouse_location: { ...form.warehouse_location, [field]: v } }); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Inward / GRN Entry</DialogTitle></DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label>Catalog Item *</Label>
            <Select value={form.catalog_id} onValueChange={function(v) { setForm({...form, catalog_id: v}); }}>
              <SelectTrigger className="mt-1" data-testid="inward-catalog-select"><SelectValue placeholder="Select item" /></SelectTrigger>
              <SelectContent>
                {catalog.map(function(c) {
                  return <SelectItem key={c.id} value={c.id}>{c.item_code} - {c.item_name}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Batch Number *</Label>
            <Input value={form.batch_number} onChange={function(e) { setForm({...form, batch_number: e.target.value}); }} className="mt-1" data-testid="inward-batch" />
          </div>
          <div>
            <Label className="text-sm font-medium">Stacking Location (same standard as Material Library)</Label>
            <div className="grid grid-cols-3 gap-3 mt-1.5">
              <LocSelect label="Warehouse *" options={master?.warehouses} value={wl.warehouse} onChange={function(v) { setWL('warehouse', v); }} testid="inward-wh" />
              <LocSelect label="Zone" options={master?.zones} value={wl.zone} onChange={function(v) { setWL('zone', v); }} />
              <LocSelect label="Aisle" options={master?.aisles} value={wl.aisle} onChange={function(v) { setWL('aisle', v); }} />
              <LocSelect label="Rack" options={master?.racks} value={wl.rack} onChange={function(v) { setWL('rack', v); }} testid="inward-rack" />
              <LocSelect label="Shelf" options={master?.shelves} value={wl.shelf} onChange={function(v) { setWL('shelf', v); }} />
              <LocSelect label="Bin" options={master?.bins} value={wl.bin} onChange={function(v) { setWL('bin', v); }} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Item Type</Label>
              <select value={form.item_type || 'profile'} onChange={function(e) { setForm({ ...form, item_type: e.target.value }); }} className="mt-1 h-10 w-full border rounded px-2 text-sm" data-testid="inward-item-type"><option value="profile">Profile</option><option value="hardware">Hardware</option></select>
            </div>
            <div><Label className="text-xs text-muted-foreground">Finish</Label><Input value={form.finish || ''} onChange={function(e) { setForm({ ...form, finish: e.target.value }); }} className="mt-1" /></div>
            <div><Label className="text-xs text-muted-foreground">Material Type</Label><Input value={form.material_type || ''} onChange={function(e) { setForm({ ...form, material_type: e.target.value }); }} className="mt-1" /></div>
            <div><Label className="text-xs text-muted-foreground">Length (m)</Label><Input type="number" value={form.length_m || 0} onChange={function(e) { setForm({ ...form, length_m: parseFloat(e.target.value) || 0 }); }} className="mt-1" disabled={form.item_type !== 'profile'} /></div>
            <div><Label className="text-xs text-muted-foreground">Price/UOM (₹)</Label><Input type="number" value={form.price_per_uom || 0} onChange={function(e) { setForm({ ...form, price_per_uom: parseFloat(e.target.value) || 0 }); }} className="mt-1" data-testid="inward-price" /></div>
            <div><Label className="text-xs text-muted-foreground">GST %</Label><Input type="number" value={form.gst_percent || 0} onChange={function(e) { setForm({ ...form, gst_percent: parseFloat(e.target.value) || 0 }); }} className="mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Quantity *</Label>
              <Input type="number" value={form.quantity} onChange={function(e) { setForm({...form, quantity: parseFloat(e.target.value) || 0}); }} className="mt-1" data-testid="inward-qty" />
            </div>
            <div>
              <Label>Unit Cost</Label>
              <Input type="number" value={form.unit_cost} onChange={function(e) { setForm({...form, unit_cost: parseFloat(e.target.value) || 0}); }} className="mt-1" />
            </div>
          </div>
          <div>
            <Label>Color/Finish</Label>
            <Input value={form.color_finish} onChange={function(e) { setForm({...form, color_finish: e.target.value}); }} className="mt-1" placeholder="e.g., RAL 9010 White" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="qc_check" checked={form.qc_passed} onChange={function(e) { setForm({...form, qc_passed: e.target.checked}); }} />
            <Label htmlFor="qc_check">QC Passed (Film Integrity OK)</Label>
          </div>
          <Button type="submit" className="w-full btn-accent" data-testid="save-inward-btn">Create Entry</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function QRDialog({ open, onOpenChange, qr }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm text-center">
        <DialogHeader><DialogTitle>QR Code: {qr?.label}</DialogTitle></DialogHeader>
        {qr && (
          <div className="py-4">
            <img src={qr.url} alt="QR Code" className="w-48 h-48 mx-auto border rounded" />
            <p className="mt-4 text-sm text-muted-foreground">Scan with your device</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
